import { useMemo } from 'react'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'

/**
 * Maps blueprint presentation columns to tanstack react-table column defs.
 * Shared by both BlueprintDataGrid and BlueprintPresentationTable.
 */
export default function useBlueprintColumnDefs (columns, footerData, footerColumnMapping) {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    if (!columns) return { columns: [], includeFooter: false }

    const includeFooter = footerData?.length > 0 && footerColumnMapping?.length > 0
    const footerRow = includeFooter ? footerData[0] : null

    const mapped = columns
      .filter(col => !col.exclude)
      .map(col => {
        const def = {
          accessorKey: col.name,
          header: col.displayName || col.name,
          align: col.align || 'left',
          defaultShown: !col.hidden,
          enableSorting: col.sortable ?? true,
          enableColumnFilter: col.filterable ?? true,
          size: col.width || 150
        }

        if (col.formatString) {
          def.cell = (props) => formatter(props.getValue(), col.formatString)
        }

        if (includeFooter) {
          const mapping = footerColumnMapping.find(m => m.mainColumn === col.name)
          if (mapping && footerRow) {
            def.footer = () => {
              const value = footerRow[mapping.footerColumn]
              return col.formatString ? formatter(value, col.formatString) : value
            }
          }
        }

        return def
      })

    return { columns: mapped, includeFooter }
  }, [columns, footerData, footerColumnMapping, formatter])
}

/**
 * Maps blueprint defaultSort `[{ field, dir }]` to tanstack
 * react-table sort format `[{ id, desc }]`.
 */
export function mapDefaultSort (defaultSort) {
  if (!defaultSort?.length) return []
  return defaultSort.map(s => ({
    id: s.field,
    desc: s.dir === 'desc'
  }))
}
